import { SyntheticEvent, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { checkAddress } from '@r1-frontend/entities/ContactAddress/actions/checkAddress';
import { setAddressEdit } from '@r1-frontend/entities/ContactAddress/actions/setAddressEdit';
import { EAvailableSpeed } from '@r1-frontend/entities/ContactAddress/initialState';
import { isAddressFilled } from '@r1-frontend/entities/ContactAddress/selectors/isAddressFilled';
import { EVerificationStatus } from '@r1-frontend/entities/ContactDetail/initialState';

import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import { GIGABIT_SPEED, MEGABIT_SPEED } from '~/src/features/full-buy/consts';
import { TNextButtonHook } from '~/src/features/full-buy/containers/GoToNextStepButton/types';
import { goToNextStep } from '~/src/features/full-buy/store/actions/goToNextStep';
import { selectCurrentTariff } from '~/src/features/full-buy/store/selectors/selectCurrentTariff';
import { selectFilteredTariffs } from '~/src/features/full-buy/store/selectors/selectFilteredTariffs';
import { TState } from '~/src/store';

const useOldAddressFormNextButton = (): TNextButtonHook => {
    const analytics = useAnalytics();
    const dispatch = useDispatch();

    const {
        selectedTariff,
        addressStatus,
        isEdit,
        isAgree,
        isConfirmButtonActive,
        verifiedAddressData: {
            isHouseConnected,
            isHouseCottage,
            availableSpeed,
        },
    } = useSelector((state: TState) => ({
        selectedTariff: selectCurrentTariff(state),
        filteredTariffs: selectFilteredTariffs(state),
        addressStatus: state.contactAddress.verifiedAddressData.status,
        isEdit: state.contactAddress.isEdit,
        isAgree: state.contactAddress.isAgree,
        isConfirmButtonActive: isAddressFilled(state),

        verifiedAddressData: state.contactAddress.verifiedAddressData,
    }), shallowEqual);

    const { speed: tariffSpeed = null } = selectedTariff || {};

    const addressIsVerified = addressStatus === EVerificationStatus.verified;

    const tariffSpeedAcrossEnum = useMemo(() => {
        if (tariffSpeed === null) {
            return null;
        }

        if (tariffSpeed >= GIGABIT_SPEED) {
            return EAvailableSpeed.more1000;
        }

        if (tariffSpeed > MEGABIT_SPEED) {
            return EAvailableSpeed.between100and1000;
        }

        return EAvailableSpeed.lessOrEqual100;
    }, [tariffSpeed]);

    const shouldConnect = isHouseConnected && (
        (Number(tariffSpeedAcrossEnum) > EAvailableSpeed.lessOrEqual100 && Number(tariffSpeedAcrossEnum) <= availableSpeed) ||
        (Number(tariffSpeedAcrossEnum) === EAvailableSpeed.lessOrEqual100)
    );

    if (addressStatus === EVerificationStatus.notVerified || isEdit) {
        return {
            disabled: !isConfirmButtonActive || !isAgree,
            onClick: (e: SyntheticEvent) => {
                e.stopPropagation();
                dispatch(checkAddress(true, selectedTariff?.isMono));
                dispatch(setAddressEdit(false));
                analytics.address.checkClicked();
            },
            children: 'Подтвердить',
        };
    } else {
        return {
            disabled: !shouldConnect || !isConfirmButtonActive || !isAgree || !addressIsVerified || !isHouseConnected || isHouseCottage,
            onClick: (e: SyntheticEvent) => {
                e.stopPropagation();
                analytics.address.continueClicked();
                dispatch(goToNextStep());
            },
            children: 'Продолжить',
        };
    }
};

export default useOldAddressFormNextButton;
